import React, { useState } from 'react';
import { cn } from '../../utils/cn';

type CurrencyInputProps = {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  currency?: string;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  min?: string;
  step?: string;
  error?: string;
};

function formatDisplay(raw: string, currency: string): string {
  const num = Number(raw);
  if (!raw || isNaN(num)) return '';
  try {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${currency} ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

export function CurrencyInput({
  label,
  value,
  onChange,
  currency = 'IDR',
  placeholder = '0',
  className,
  inputClassName,
  min = '0',
  step = 'any',
  error,
}: CurrencyInputProps) {
  const [focused, setFocused] = useState(false);

  const inputStyles = cn(
    'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm outline-none transition dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500',
    'focus:border-red-400 focus:ring-2 focus:ring-red-200',
    error ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-200' : '',
    inputClassName,
  );

  return (
    <label className={cn('block', className)}>
      {label ? (
        <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{label}</div>
      ) : null}
      {focused ? (
        <input
          type="number"
          className={inputStyles}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          min={min}
          step={step}
          autoFocus
        />
      ) : (
        <div
          className={cn(inputStyles, 'cursor-text', !value && 'text-slate-400')}
          onClick={() => setFocused(true)}
          onFocus={() => setFocused(true)}
          tabIndex={0}
        >
          {value ? formatDisplay(value, currency) : placeholder}
        </div>
      )}
      {error ? (
        <div className="mt-1 text-xs text-rose-600">{error}</div>
      ) : null}
    </label>
  );
}
